/**
 * COMPTABILITÉ — LE COCKPIT.
 *
 * Ce n'est pas une page d'accueil : c'est un poste de travail. Chaque bloc
 * répond à « qu'est-ce que je dois faire maintenant ? » et porte le geste qui
 * y répond. Un indicateur qu'on regarde sans jamais agir dessus a été écarté :
 * il occupe la place et fait passer le reste pour du décor.
 *
 * Ce qui s'y trouve, et rien d'autre :
 *   · les SEPT DERNIÈRES NUITS, et « commencer la clôture » sur la plus
 *     ancienne qui reste à faire — l'action du matin. Chaque carte porte les
 *     chiffres du bandeau de sa fiche de nuit : en gros le « Rapport », au-
 *     dessus et plus petit le « Chiffre d'affaires » quand il est tapé
 *     (`ChiffresDeNuit`) ;
 *   · les ÉCARTS restés OUVERTS — chacun est une question sans réponse ;
 *   · l'ÉTAT DES SOURCES — un document non lu ne deviendra jamais un chiffre ;
 *   · le RETENU du mois — issu des seules nuits FINALISÉES, et il dit combien
 *     de nuits manquent encore ;
 *   · les ESPÈCES à déposer.
 *
 * ═══ HONNÊTETÉ ═══
 * Une donnée absente affiche « — » ou dit pourquoi elle manque ; jamais
 * « 0 € ». Le total du mois n'est pas « le chiffre d'affaires du mois » : c'est
 * le retenu des nuits déjà arrêtées, et les nuits qui manquent sont comptées à
 * côté — sans quoi le premier jour du mois afficherait un effondrement.
 */

import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import {
  AlertTriangle, ArrowRight, BadgeCheck, Banknote, Calculator, CalendarDays, CheckCircle2, ChevronRight,
  FilePlus2, FileWarning, Inbox, Moon, PenLine, Plus, ShoppingBasket, TrendingUp, X,
} from 'lucide-react'
import { Link, navigate } from '../router.js'
import { Modal, adminMutate, usePermissions, useToast } from '../admin/primitives.js'
import { formatMoney } from './money.js'
import {
  CLOSING_STATUS_LABELS, EnCours, Indisponible, Montant,
  SOURCE_PARSE_LABELS, SOURCE_PARSE_PILL, SOURCE_PROVIDER_LABELS,
  aReprendre, ajouterJours, aujourdhuiParis, commeLignes, ilYA, lireJson,
  nuitCourte, nuitLongue, objetSous, tableauSous,
  type ClosingRow, type Etat, type SourceDocumentRow, type SourceProvider,
} from './ClosingCommon.js'
import {
  EtatNuit, NIGHT_PAINT, commeNuits, lireClotureDeNuit, lireFicheNuit, urlCloture, type NightRow, type NightsResponse,
} from './nights-common.js'
import {
  MONTANTS_NON_LUS, fileDeLectures, lireChiffresDeCarte, type LecteursDeNuit, type LectureCarte,
} from './carte-nuit-lecture.js'
import { CA_PAS_ENCORE_COMPTE, LIBELLE_CA_CARTE } from './night-sheet-model.js'
import { repriseDe } from './reprise-historique.js'
import { DepotRapports } from './SourcesDepot.js'
import { adresseSources } from './sources-adresse.js'
import { nuitDeLigne, useMenuSoiree } from '../soirees/menu-soiree.js'
import './finance-nights.css'
// Le bloc « Sources reçues » est le raccourci de l'écran des sources : il en
// emprunte la mise en page du rappel (`.fis-cockpit`), pas une règle de plus.
import './finance-sources.css'

/* ═══════════════════════ ce que le cockpit va lire ═══════════════════════ */

/**
 * LES ESPÈCES EN ATTENTE DE REMISE.
 *
 * `/deposits/cash-to-deposit` distingue deux montants que l'écran ne doit
 * jamais confondre : ce qui a été COMPTÉ physiquement, et la recette espèces
 * RECONNUE par la clôture. Ce qui reste à porter en banque est
 * `total_remaining_cents` — c'est lui, et lui seul, qui répond à « combien
 * reste-t-il à déposer ? ».
 */
interface DepotsVue { restant_cents: number | null; nuits: number }

function commeDepots(v: unknown): DepotsVue | null {
  const o = objetSous(v)
  if (!o) return null
  const brut = o['total_remaining_cents']
  const restant = typeof brut === 'number' ? brut : typeof brut === 'string' && brut !== '' ? Number(brut) : null
  if (restant === null || !Number.isFinite(restant)) return null
  return { restant_cents: restant, nuits: (tableauSous(v, 'nights') ?? []).length }
}

const PROVIDERS_SUIVIS: SourceProvider[] = ['ZAP_SIS', 'SHOTGUN']
const ROLE_SOURCE: Partial<Record<SourceProvider, string>> = {
  ZAP_SIS: 'Rapports de caisse · bars et vestiaire',
  SHOTGUN: 'Redditions · billetterie et Accueil',
}

/** « samedi 3 octobre 2026 » → « Samedi 3 octobre 2026 ». */
const majuscule = (t: string): string => (t.length > 0 ? t[0]!.toUpperCase() + t.slice(1) : t)

const FMT_SEMAINE = new Intl.DateTimeFormat('fr-FR', { weekday: 'long', timeZone: 'UTC' })
const FMT_MOIS = new Intl.DateTimeFormat('fr-FR', { month: 'long', timeZone: 'UTC' })
/** Les morceaux d'une date de carte : « 3 », « samedi », « octobre ». */
function dateCarte(nuit: string): { jour: string; semaine: string; mois: string } {
  const d = new Date(`${nuit}T12:00:00Z`)
  return { jour: String(d.getUTCDate()), semaine: majuscule(FMT_SEMAINE.format(d)), mois: FMT_MOIS.format(d) }
}

/**
 * Combien de documents le cockpit lit pour se faire une idée. Il n'y a AUCUNE
 * borne de date : `listDocuments` filtre sur la nuit commerciale dès qu'on lui
 * en donne une, et un document dont la nuit n'a pas pu être déduite en
 * disparaîtrait — c'est précisément celui qu'il faut voir. Le serveur rend
 * d'ailleurs les documents sans nuit EN PREMIER.
 */
const SOURCES_LUES = 100

/** Le document le plus récent d'un fournisseur, à l'instant de réception. */
function dernierDe(docs: SourceDocumentRow[], p: SourceProvider): SourceDocumentRow | null {
  let out: SourceDocumentRow | null = null
  let quand = Number.NEGATIVE_INFINITY
  for (const d of docs) {
    if (d.provider !== p) continue
    const t = Date.parse(d.received_at)
    if (!Number.isFinite(t)) continue
    if (t > quand) { quand = t; out = d }
  }
  return out
}

/**
 * QUI A ÉCRIT EN DERNIER DANS LA NUIT — la question du matin dans une équipe
 * de plusieurs : « quelqu'un est déjà dessus ? ». La carte disait l'état de
 * la clôture (« Brouillon ») sans dire de qui il était : il fallait l'ouvrir
 * pour le savoir.
 *
 * Le nom vient du SERVEUR (la dernière écriture pose son auteur sur la
 * clôture) ; l'écran ne le déduit de rien. Une nuit VALIDÉE sans clôture dit
 * qui l'a validée : c'est la même question, posée à l'autre geste. Quand rien
 * n'est connu, la carte se tait plutôt que d'écrire « par — ».
 */
function derniereMain(n: NightRow): { icone: 'valide' | 'modifie'; texte: string; quand: string | null } | null {
  // `?? null` : un serveur pas encore à jour n'envoie pas ces champs du tout,
  // et la carte doit alors se taire plutôt que de tomber.
  const v = n.validation ?? null
  if (n.state === 'VALIDEE' && v !== null) {
    // Reprise de l'historique : ni « par » ni « il y a » — l'heure de l'import
    // n'est pas celle d'un geste sur cette nuit.
    const reprise = repriseDe(v.history)
    if (reprise !== null) return { icone: 'valide', texte: reprise.label, quand: null }
    const qui = v.validated_by_name ? ` par ${v.validated_by_name}` : ''
    return { icone: 'valide', texte: `Validée${qui}`, quand: ilYA(v.validated_at) }
  }
  const c = n.closing ?? null
  if (c === null || (!c.last_editor_name && !c.last_edited_at)) return null
  const qui = c.last_editor_name ? ` par ${c.last_editor_name}` : ''
  // « il y a 44 min » reste d'un bloc : coupé après « il », il se lisait mal.
  return { icone: 'modifie', texte: `Modifiée${qui}`, quand: c.last_edited_at ? ilYA(c.last_edited_at) : null }
}

/** « Samedi 3 octobre 2026 » au milieu d'une phrase : « samedi 3 octobre 2026 ». */
const minuscule = (t: string): string => t.charAt(0).toLocaleLowerCase('fr') + t.slice(1)

/** Somme de centimes qui peuvent arriver en chaîne (bigint PostgreSQL). */
function sommeRetenue(rows: ClosingRow[]): number {
  return rows.reduce((t, c) => {
    const n = typeof c.retained_cents === 'string' ? Number(c.retained_cents) : c.retained_cents
    return t + (typeof n === 'number' && Number.isFinite(n) ? n : 0)
  }, 0)
}

/**
 * Une nuit VALIDÉE SANS CLÔTURE DÉTAILLÉE (`validated_nights` de
 * `GET /closings`) : elle n'est jamais dans `closings`, et son montant vaut
 * null quand le rôle ne lit pas les sources — on l'écrit alors « — ».
 */
interface NuitValidee { business_date: string; revenue_cents: number | string | null; entries: number }
interface ClosingsVue { lignes: ClosingRow[]; validees: NuitValidee[] }

function commeClosings(v: unknown): ClosingsVue | null {
  const lignes = commeLignes<ClosingRow>(['closings', 'items'], 'business_date')(v)
  if (!lignes) return null
  return { lignes, validees: commeLignes<NuitValidee>(['validated_nights'], 'business_date')(v) ?? [] }
}

/** Le montant validé d'un ensemble de nuits ; null dès qu'un seul est masqué (jamais un 0 inventé). */
function sommeValidee(rows: NuitValidee[]): number | null {
  let t = 0
  for (const v of rows) {
    const n = typeof v.revenue_cents === 'string' ? Number(v.revenue_cents) : v.revenue_cents
    if (typeof n !== 'number' || !Number.isFinite(n)) return null
    t += n
  }
  return t
}

/**
 * LES CHIFFRES D'UNE CARTE « LES DERNIÈRES SOIRÉES » (10/10/2026) — ceux du
 * bandeau de la fiche de la nuit, calculés par le même modèle
 * (`chiffresDeLaCarte` → `feuilleDeCaisse`, `chiffreAffairesDuBandeau`) : en
 * gros le « Rapport », au-dessus et plus petit le « Chiffre d'affaires »
 * quand il est TAPÉ (compté, arrêté, validé) — « CA pas encore compté » quand
 * la fiche ne l'estime que d'après les rapports.
 *
 * Chaque carte lit SA nuit comme la fiche la lit, avec les mêmes lectures
 * (`lireChiffresDeCarte` : `lireFicheNuit`, puis `lireClotureDeNuit` quand
 * une clôture existe), sans retenir l'affichage — la date, la soirée et
 * l'état paraissent tout de suite, les montants dès qu'ils arrivent. En
 * attendant, une barre neutre : ni « — » (absent), ni « 0 € ».
 *
 * Les lectures passent par une file partagée (`FILE_DES_CARTES`) : deux
 * nuits à la fois, dans l'ordre des cartes (11/10/2026). Les sept d'un coup
 * faisaient jusqu'à quatorze lectures lourdes en même temps, sur un pool de
 * dix connexions et un quota commun à tous les postes. Une carte démontée ou
 * relue annule les siennes (`AbortController`) : rien ne s'empile.
 *
 * Les droits sont ceux de la fiche. Sans `finance.source.read`, rien n'est lu
 * et le rapport s'écrit « — », comme sur la fiche (« Montants non lus »). Un
 * guichet ou des préventes cachés faute du droit de lire la billetterie
 * emportent les totaux dans le modèle : jamais un chiffre partiel présenté
 * comme complet. Une lecture qui échoue — la clôture comprise — fait taire
 * la carte. Un chiffre d'affaires absent ne s'écrit pas du tout.
 */
const FILE_DES_CARTES = fileDeLectures(2)
const LECTEURS: LecteursDeNuit = { fiche: lireFicheNuit, cloture: lireClotureDeNuit }

function ChiffresDeNuit(props: { date: string; peutLire: boolean; droitsEnCours: boolean; relecture: number }) {
  const { date, peutLire, droitsEnCours, relecture } = props
  const [lu, setLu] = useState<LectureCarte>({ etat: 'chargement' })

  // Relue à chaque relecture du cockpit (`relecture` : un dépôt, « Réessayer ») ;
  // pendant la relecture, la carte garde ses chiffres plutôt que de clignoter.
  useEffect(() => {
    if (droitsEnCours) return
    if (!peutLire) { setLu({ etat: 'muet', aide: MONTANTS_NON_LUS }); return }
    const arret = new AbortController()
    void FILE_DES_CARTES(() => lireChiffresDeCarte(date, LECTEURS, arret.signal), arret.signal)
      .then((r) => { if (r !== null && !arret.signal.aborted) setLu(r) })
    return () => arret.abort()
  }, [date, peutLire, droitsEnCours, relecture])

  // Deux lignes de deux colonnes, libellé puis montant, dans TOUS les états :
  // la carte garde sa hauteur quand ses chiffres arrivent (la rangée de la
  // grille est partagée : une carte qui grandit pousse ses voisines).
  if (lu.etat === 'chargement') {
    return (
      <span className="fn-carte__chiffres" aria-busy="true">
        <span className="u-sr">Montants en cours de lecture</span>
        <span className="fn-carte__libelle" />
        <span className="fn-carte__ca" />
        <span className="fn-carte__libelle">Rapport</span>
        <span className="fn-carte__montant fn-carte__attente" aria-hidden="true">{'\u00a0'}</span>
      </span>
    )
  }
  const c = lu.etat === 'ok' ? lu.chiffres : null
  const ca = c?.ca ?? null
  const enAttente = c?.caEnAttente ?? null
  return (
    <span className="fn-carte__chiffres">
      <span className="fn-carte__libelle">{ca || enAttente ? LIBELLE_CA_CARTE : null}</span>
      {ca ? (
        <span className="fn-carte__ca fi-money" title={ca.aide}>{formatMoney(ca.valeur)}</span>
      ) : (
        <span className="fn-carte__ca fn-carte__ca--attente" title={enAttente ?? undefined}>{enAttente ? CA_PAS_ENCORE_COMPTE : null}</span>
      )}
      <span className="fn-carte__libelle">Rapport</span>
      <span className="fn-carte__montant fi-money" title={c === null ? (lu.etat === 'muet' ? lu.aide : undefined) : c.rapport === null ? c.aideRapport : undefined}>
        {formatMoney(c?.rapport ?? null)}
      </span>
    </span>
  )
}

export function FinanceHome() {
  const toast = useToast()
  const { permissions, loading: droitsEnCours } = usePermissions()
  const peutOuvrir = permissions.includes('finance.closure.create')
  // Le droit de la fiche de nuit pour ses montants (et pour lire la clôture) :
  // les cartes, qui écrivent les mêmes chiffres, suivent le même.
  const peutLireMontants = permissions.includes('finance.source.read')
  // Même droit que la zone de dépôt de l'écran des sources, et que la route
  // `POST /finance/sources/drop` : un bouton offert à qui serait refusé ne
  // serait qu'une promesse cassée.
  const peutDeposer = permissions.includes('finance.source.manage')
  const peutVoirVentes = permissions.includes('finance.revenue.read')
  const menuSoiree = useMenuSoiree()

  const [nuits, setNuits] = useState<Etat<NightsResponse>>({ etat: 'chargement' })
  const [closings, setClosings] = useState<Etat<ClosingsVue>>({ etat: 'chargement' })
  const [sources, setSources] = useState<Etat<SourceDocumentRow[]>>({ etat: 'chargement' })
  const [depots, setDepots] = useState<Etat<DepotsVue>>({ etat: 'chargement' })
  // Chaque relecture du cockpit relit aussi les chiffres des cartes : un
  // rapport déposé d'ici change le « Rapport » et le chiffre d'affaires.
  const [relecture, setRelecture] = useState(0)
  const [ouverture, setOuverture] = useState(false)
  /* La fenêtre « Ajouter des sources ». Elle ne se ferme pas PENDANT un dépôt :
     les fichiers partent un par un, et fermer ferait perdre le verdict de
     chacun (nuit, doublon, refus) alors que l'envoi, lui, continuerait. */
  const [depotOuvert, setDepotOuvert] = useState(false)
  const [depotEnCours, setDepotEnCours] = useState(false)
  const [depotFait, setDepotFait] = useState(false)
  // La pile des fenêtres (Échap) garde la fonction de fermeture reçue À
  // L'OUVERTURE : elle ne verrait jamais l'état « en cours » posé ensuite.
  // Le verrou passe donc par une référence, que toutes les copies lisent.
  const depotEnCoursRef = useRef(false)

  const fenetre = useMemo(() => {
    const fin = aujourdhuiParis()
    return { debut: ajouterJours(fin, -120), fin }
  }, [])

  const charger = useCallback(() => {
    // Une fenêtre un peu plus large que sept jours : c'est le SERVEUR qui dit
    // quelle nuit est la nuit courante (avant 8 h, c'est encore la veille).
    // Un mois : de quoi trouver sept nuits AVEC une activité, même après des congés.
    void lireJson<NightsResponse>(`/api/v1/finance/nights?from=${ajouterJours(fenetre.fin, -45)}&to=${fenetre.fin}`, commeNuits).then(setNuits)
    void lireJson<ClosingsVue>(
      `/api/v1/finance/closings?from=${fenetre.debut}&to=${fenetre.fin}`,
      commeClosings,
    ).then(setClosings)
    void lireJson<SourceDocumentRow[]>(
      `/api/v1/finance/sources?limit=${SOURCES_LUES}`,
      commeLignes(['documents', 'sources', 'items'], 'filename'),
    ).then(setSources)
    void lireJson<DepotsVue>('/api/v1/finance/deposits/cash-to-deposit', commeDepots).then(setDepots)
    setRelecture((x) => x + 1)
  }, [fenetre])

  useEffect(charger, [charger])

  /* ─── ouvrir la clôture de la nuit ─── */
  const ouvrir = async (businessDate: string) => {
    setOuverture(true)
    const r = await adminMutate<{ error?: string; id?: string; message?: string }>(
      'POST', '/api/v1/finance/closings', { business_date: businessDate },
    )
    setOuverture(false)
    if (r.status >= 200 && r.status < 300 && typeof r.data.id === 'string') {
      toast('ok', 'Clôture ouverte. L’assistant reprendra exactement où vous vous arrêterez.')
      navigate(urlCloture(businessDate))
      return
    }
    toast('error', r.data.message ?? 'La clôture n’a pas pu être ouverte.')
  }

  /* ─── ajouter des sources sans quitter l'accueil ─── */
  const ouvrirDepot = () => { setDepotFait(false); setDepotOuvert(true) }
  const fermerDepot = () => { if (!depotEnCoursRef.current) setDepotOuvert(false) }
  const signalerDepot = (occupe: boolean) => { depotEnCoursRef.current = occupe; setDepotEnCours(occupe) }
  // Un dépôt change plus que la liste des documents : un rapport ZAP fait
  // apparaître le montant d'une nuit et peut changer son état. On relit donc
  // TOUT le cockpit, pas seulement le bloc des sources.
  const depotTermine = () => { setDepotFait(true); charger() }

  /* ─── ce qu'on déduit, sans jamais inventer ─── */
  const lignes = closings.etat === 'ok' ? closings.valeur.lignes : []
  const derniere = lignes.find((c) => c.status === 'CLOSED') ?? null
  const avecEcarts = lignes.filter((c) => c.open_discrepancies > 0)
  const mois = fenetre.fin.slice(0, 7)
  const duMois = lignes.filter((c) => c.business_date.slice(0, 7) === mois)
  const closesDuMois = duMois.filter((c) => c.status === 'CLOSED')
  // Une nuit validée sans clôture détaillée COMPTE dans le retenu du mois —
  // nommée à part — et son éventuel brouillon n'est plus « à finaliser ».
  const valideesDuMois = closings.etat === 'ok'
    ? closings.valeur.validees.filter((v) => v.business_date.slice(0, 7) === mois) : []
  const datesValidees = new Set(valideesDuMois.map((v) => v.business_date))
  const montantValide = sommeValidee(valideesDuMois)
  const restantesDuMois = duMois.filter((c) => (c.status === 'DRAFT' || c.status === 'READY') && !datesValidees.has(c.business_date))

  const docs = sources.etat === 'ok' ? sources.valeur : []
  // « À reprendre » est décidé par le serveur : ni les annexes ZAP (des
  // preuves, pas des tâches), ni les rapports écartés.
  const aVerifier = docs.filter(aReprendre)

  // Les nuits OÙ IL S'EST PASSÉ QUELQUE CHOSE — une soirée, un rapport, une
  // clôture —, les plus récentes d'abord ; jamais une nuit à venir. Une nuit
  // fermée n'apprend rien, et « nuit en cours » sans soirée ne veut rien dire
  // (retour de l'exploitant, 07/10/2026).
  const reponse = nuits.etat === 'ok' ? nuits.valeur : null
  const active = (n: NightsResponse['nights'][number]): boolean =>
    n.soirees.some((s) => s.status === 'SCHEDULED') || n.zap.principal_count + n.zap.complement_count > 0
    || n.closing !== null || n.shotgun_report_count > 0
  const sept = reponse
    ? reponse.nights.filter((n) => n.business_date <= reponse.current_night && active(n)).slice(-7).reverse()
    : []
  const aTraiter = reponse?.oldest_pending ?? null
  // Les ventes du guichet rattachées à une autre nuit et pas encore décidées :
  // un cas paraît sur ses DEUX nuits (vente et soirée), on le compte une fois.
  // On mène à la nuit de VENTE, où se décide le rattachement.
  const guichetADecider = reponse === null ? [] : [...new Map(reponse.nights
    .flatMap((n) => n.off_night_sales ?? [])
    .filter((c) => c.blocking)
    .map((c) => [c.key, c] as const)).values()]
    .sort((a, b) => a.sale_business_date.localeCompare(b.sale_business_date))
  const rienAFaire =
    reponse !== null && aTraiter === null &&
    closings.etat === 'ok' && avecEcarts.length === 0 &&
    sources.etat === 'ok' && aVerifier.length === 0 && restantesDuMois.length === 0

  return (
    <main className="lc-page lc-page--large lc-stagger fh-page">
      <header className="fh-hero">
        <span className="fh-hero__icon" aria-hidden="true"><Calculator size={26} /></span>
        <div>
          <h1 className="lc-title">Comptabilité</h1>
          <p className="lc-subtitle">
            Ce qui attend une décision. Chaque chiffre arrive avec sa source, et
            rien n’est recopié d’un tableau à l’autre.
          </p>
        </div>
      </header>

      {/* ═══════════ LES SEPT DERNIÈRES NUITS ═══════════ */}
      <section className="lc-panel fih-nuit">
        <div className="lc-panel__head">
          <span className="fih-head__icon" aria-hidden="true"><Moon size={17} /></span>
          <h2 className="lc-panel__title">Les dernières soirées</h2>
          <span className="lc-panel__spacer" />
          {peutVoirVentes ? (
            <Link className="lc-btn lc-btn--ghost lc-btn--sm" href="/comptabilite/ventes">
              <ShoppingBasket size={15} aria-hidden="true" /> Ventes &amp; produits
            </Link>
          ) : null}
          <Link className="lc-btn lc-btn--ghost lc-btn--sm" href="/comptabilite/nuits">
            <CalendarDays size={15} aria-hidden="true" /> Calendrier des nuits
          </Link>
        </div>
        <div className="lc-panel__body">
          {nuits.etat === 'chargement' ? <EnCours quoi="des dernières nuits" /> : null}
          {nuits.etat === 'absent' ? (
            <Indisponible titre="Les dernières nuits n’ont pas pu être lues" message={nuits.message}>
              <button className="lc-btn lc-btn--sm fih-retry" onClick={charger}>Réessayer</button>
            </Indisponible>
          ) : null}
          {nuits.etat === 'ok' ? (
            <>
              {guichetADecider.length > 0 ? (
                <ul className="fih-guichet" aria-label="Ventes du guichet à décider">
                  {guichetADecider.map((c) => (
                    <li key={c.key} className="fi-reserve">
                      <AlertTriangle size={15} aria-hidden="true" />
                      <span>
                        {c.tickets} billet{c.tickets > 1 ? 's' : ''} vendu{c.tickets > 1 ? 's' : ''} la nuit du{' '}
                        {minuscule(nuitLongue(c.sale_business_date))} {c.tickets > 1 ? 'sont rattachés' : 'est rattaché'} à la soirée
                        « {c.from_event_name} » du {minuscule(nuitLongue(c.from_business_date))} : la caisse Shotgun était restée ouverte.
                        Les deux nuits restent fausses tant que rien n’est décidé.{' '}
                        <Link href={`/comptabilite/nuits/${c.sale_business_date}`}>Décider</Link>
                      </span>
                    </li>
                  ))}
                </ul>
              ) : null}
              {aTraiter ? (
                <div className="fn-afaire__corps fn-commencer">
                  <EtatNuit state={aTraiter.state} libelle={aTraiter.state_label} />
                  <div className="fn-afaire__texte">
                    <p className="fn-afaire__surtitre">Plus ancienne nuit à traiter</p>
                    <p className="fn-afaire__titre">{majuscule(nuitLongue(aTraiter.business_date))}</p>
                    <p className="lc-hint">
                      {aTraiter.soirees.length > 0 ? aTraiter.soirees.map((s) => s.name).join(' · ') : 'Aucune soirée déclarée'}
                    </p>
                  </div>
                  {aTraiter.closing ? (
                    <Link className="lc-btn lc-btn--primary lc-btn--lg" href={urlCloture(aTraiter.business_date, aTraiter.soirees)} {...menuSoiree.nuit(nuitDeLigne(aTraiter))}>
                      Reprendre la clôture <ArrowRight size={16} aria-hidden="true" />
                    </Link>
                  ) : aTraiter.state === 'SOIREE_SANS_RAPPORT' || !peutOuvrir ? (
                    <Link className="lc-btn lc-btn--primary lc-btn--lg" href={`/comptabilite/nuits/${aTraiter.business_date}`} {...menuSoiree.nuit(nuitDeLigne(aTraiter))}>
                      Ouvrir la nuit <ArrowRight size={16} aria-hidden="true" />
                    </Link>
                  ) : (
                    <button
                      className="lc-btn lc-btn--primary lc-btn--lg" {...menuSoiree.nuit(nuitDeLigne(aTraiter))}
                      disabled={ouverture}
                      onClick={() => void ouvrir(aTraiter.business_date)}
                    >
                      <Plus size={17} aria-hidden="true" />{ouverture ? 'Ouverture en cours' : 'Commencer la clôture'}
                    </button>
                  )}
                </div>
              ) : (
                <p className="fi-empty fn-commencer">
                  <CheckCircle2 size={16} aria-hidden="true" /> Aucune nuit n’attend de clôture.
                </p>
              )}
              <ul className="fn-cartes">
                {sept.map((n) => {
                  const d = dateCarte(n.business_date)
                  const soiree = n.soirees.find((s) => s.status === 'SCHEDULED') ?? n.soirees[0] ?? null
                  const main = derniereMain(n)
                  return (
                    <li key={n.business_date}>
                      <Link className={`fn-carte fn-ton--${NIGHT_PAINT[n.state].tone}`} href={`/comptabilite/nuits/${n.business_date}`} {...menuSoiree.nuit(nuitDeLigne(n))}>
                        <span className="fn-carte__date">
                          <span className="fn-carte__num">{d.jour}</span>
                          <span className="fn-carte__jour">
                            <span>{d.semaine}</span>
                            <span>{d.mois}</span>
                          </span>
                        </span>
                        <span className="fn-carte__soiree">{soiree ? soiree.name : 'Aucune soirée déclarée'}</span>
                        <ChiffresDeNuit date={n.business_date} peutLire={peutLireMontants} droitsEnCours={droitsEnCours} relecture={relecture} />
                        <span className="fn-carte__bas">
                          <EtatNuit state={n.state} libelle={n.state_label} />
                          <span className="fn-carte__cloture">
                            {n.closing ? CLOSING_STATUS_LABELS[n.closing.status] : n.zap.principal_count > 0 ? 'Rapport reçu' : 'Pas de rapport'}
                          </span>
                          {main ? (
                            <span className="fn-carte__main">
                              {main.icone === 'valide'
                                ? <BadgeCheck size={14} aria-hidden="true" />
                                : <PenLine size={14} aria-hidden="true" />}
                              <span>
                                {main.texte}
                                {main.quand ? <> · <span className="fn-carte__quand">{main.quand}</span></> : null}
                              </span>
                            </span>
                          ) : null}
                        </span>
                      </Link>
                    </li>
                  )
                })}
              </ul>
              <p className="lc-hint fn-cartes__note">
                En gros, le « Rapport » de la fiche de la nuit : ce que les rapports annoncent sur place — bars,
                vestiaire et Accueil —, hors offerts. Au-dessus, « CA » : le chiffre d’affaires tapé — compté sur
                place, arrêté à la finalisation ou validé —, préventes reversées par Shotgun comprises. Tant que la nuit n’est pas
                comptée en entier, la carte écrit « CA pas encore compté ».
              </p>
            </>
          ) : null}
        </div>
      </section>

      {/* ═══════════ RIEN À FAIRE : ON LE DIT, ET DEPUIS QUAND ═══════════ */}
      {rienAFaire ? (
        <section className="lc-panel">
          <div className="lc-panel__body">
            <div className="fi-empty">
              <div className="fi-empty__title">
                <CheckCircle2 size={17} aria-hidden="true" /> Tout est à jour
              </div>
              <p>
                Toutes les nuits suivies sont clôturées, aucun écart n’est resté
                ouvert et tous les rapports reçus ont été lus.
              </p>
              {derniere?.closed_at ? (
                <p className="lc-hint">
                  Dernière finalisation {ilYA(derniere.closed_at)}
                  {derniere.closed_by_name ? ` par ${derniere.closed_by_name}` : ''}.
                </p>
              ) : null}
            </div>
          </div>
        </section>
      ) : null}

      {/* ═══════════ LES BLOCS D'ACTION ═══════════ */}
      <div className="fih-grille lc-stagger">

        {/* Écarts restés ouverts */}
        <section className="lc-panel fih-bloc">
          <div className="lc-panel__head">
            <span className="fih-head__icon" aria-hidden="true"><AlertTriangle size={17} /></span>
            <h2 className="lc-panel__title">Écarts restés ouverts</h2>
          </div>
          <div className="lc-panel__body">
            {closings.etat === 'chargement' ? <EnCours quoi="des clôtures" /> : null}
            {closings.etat === 'absent'
              ? <Indisponible titre="Clôtures indisponibles" message={closings.message} />
              : null}
            {closings.etat === 'ok' && avecEcarts.length === 0 ? (
              <p className="fi-empty">Aucun écart n’attend d’explication sur les 120 dernières nuits.</p>
            ) : null}
            {avecEcarts.length > 0 ? (
              <>
                <ul className="fih-liste">
                  {avecEcarts.slice(0, 6).map((c) => (
                    <li key={c.id}>
                      <Link className="fih-liste__lien" href={urlCloture(c.business_date)} {...menuSoiree.nuit({ date: c.business_date, cloture: { statut: c.status } })}>
                        <span className="fih-liste__titre">{nuitCourte(c.business_date)}</span>
                        <span className="lc-pill lc-pill--warning">
                          {c.open_discrepancies} écart{c.open_discrepancies > 1 ? 's' : ''}
                        </span>
                        <span className="lc-hint">{CLOSING_STATUS_LABELS[c.status]}</span>
                      </Link>
                    </li>
                  ))}
                </ul>
                <p className="lc-hint">
                  Un écart est un fait comptable à expliquer. Ce n’est jamais une
                  note portée sur quelqu’un.
                </p>
              </>
            ) : null}
          </div>
        </section>

        {/* État des sources — « à vérifier » MÈNE quelque part, sinon c'est un décor */}
        <section className="lc-panel fih-bloc">
          <div className="lc-panel__head">
            <span className="fih-head__icon" aria-hidden="true"><Inbox size={17} /></span>
            <h2 className="lc-panel__title">Sources reçues</h2>
            <span className="lc-panel__spacer" />
            <Link className="lc-btn lc-btn--ghost lc-btn--sm" href="/comptabilite/sources">Tous les documents</Link>
            {peutDeposer ? (
              <button type="button" className="lc-btn lc-btn--primary lc-btn--sm" onClick={ouvrirDepot}>
                <FilePlus2 size={15} aria-hidden="true" /> Ajouter des sources
              </button>
            ) : null}
          </div>
          <div className="lc-panel__body">
            {sources.etat === 'chargement' ? <EnCours quoi="des documents reçus" /> : null}
            {sources.etat === 'absent'
              ? <Indisponible titre="Documents sources indisponibles" message={sources.message} />
              : null}
            {sources.etat === 'ok' ? (
              <>
                {/* Chaque tuile MÈNE à la liste de son fournisseur, toutes vues
                    confondues (`vue=tout`) : la vue par défaut, « à vérifier »,
                    serait vide la plupart du temps, et la tuile semblerait
                    ne mener nulle part. */}
                <ul className="fih-sources">
                  {PROVIDERS_SUIVIS.map((p) => {
                    const dernier = dernierDe(docs, p)
                    const frais = dernier !== null && Date.now() - Date.parse(dernier.received_at) < 4 * 24 * 3600 * 1000
                    return (
                      <li key={p}>
                        <Link className="fih-source" href={adresseSources(p)}>
                          <span className={`fih-source__point${frais ? ' fih-source__point--ok' : ''}`} aria-hidden="true" />
                          <span className="fih-source__texte">
                            <span className="fih-source__nom">{SOURCE_PROVIDER_LABELS[p]}</span>
                            <span className="fih-source__role">{ROLE_SOURCE[p]}</span>
                          </span>
                          <span className="fih-source__quand">
                            {dernier ? <>Dernier reçu<br />{ilYA(dernier.received_at)}</> : 'Rien de récent'}
                          </span>
                          <ChevronRight size={16} className="fih-source__fleche" aria-hidden="true" />
                          <span className="u-sr">Voir les documents {SOURCE_PROVIDER_LABELS[p]}</span>
                        </Link>
                      </li>
                    )
                  })}
                </ul>
                {aVerifier.length > 0 ? (
                  <>
                    <div className="fih-alerte">
                      <FileWarning size={16} aria-hidden="true" />
                      <span>
                        {aVerifier.length} document{aVerifier.length > 1 ? 's' : ''} à
                        reprendre : un document que Gestion n’a pas su lire ne produit
                        aucun chiffre, et n’en produira pas tout seul.
                      </span>
                    </div>
                    {/* Chaque document mène à SA page : la raison y est écrite,
                        et la correspondance manquante s'y déclare. */}
                    <div className="fis-cockpit">
                      <ul className="fih-liste">
                        {aVerifier.slice(0, 3).map((d) => (
                          <li key={d.id}>
                            <Link className="fih-liste__lien" href={`/comptabilite/sources/${d.id}`}>
                              <span className="fih-liste__titre">
                                {d.business_date === null ? 'Sans nuit' : nuitCourte(d.business_date)}
                              </span>
                              <span className={SOURCE_PARSE_PILL[d.parse_status] ?? 'lc-pill lc-pill--idle'}>
                                {SOURCE_PARSE_LABELS[d.parse_status] ?? d.parse_status}
                              </span>
                              <span className="lc-hint">{d.filename}</span>
                            </Link>
                          </li>
                        ))}
                      </ul>
                      <Link className="lc-btn lc-btn--primary" href="/comptabilite/sources">
                        Reprendre {aVerifier.length > 1 ? 'ces documents' : 'ce document'}
                      </Link>
                    </div>
                  </>
                ) : (
                  <p className="fih-sources__ok"><CheckCircle2 size={15} aria-hidden="true" /> Tous les documents reçus ont été lus.</p>
                )}
              </>
            ) : null}
          </div>
        </section>

        {/* Retenu du mois — les nuits FINALISÉES, jamais une projection */}
        <section className="lc-panel fih-bloc">
          <div className="lc-panel__head">
            <span className="fih-head__icon" aria-hidden="true"><TrendingUp size={17} /></span>
            <h2 className="lc-panel__title">Retenu ce mois-ci</h2>
            <span className="lc-panel__spacer" />
            <Link className="lc-btn lc-btn--ghost lc-btn--sm" href="/comptabilite/chiffre-affaires">Le détail</Link>
          </div>
          <div className="lc-panel__body">
            {closings.etat !== 'ok' ? (
              <Indisponible
                titre="Montant du mois indisponible"
                message={closings.etat === 'absent' ? closings.message : 'Lecture en cours.'}
              />
            ) : closesDuMois.length === 0 && valideesDuMois.length === 0 ? (
              <p className="fi-empty">
                Aucune nuit de ce mois n’est encore finalisée. Le montant paraîtra
                dès la première clôture arrêtée — pas avant.
              </p>
            ) : (
              <>
                {/* Seules des nuits validées, au montant illisible pour ce rôle :
                    le total est INCONNU (« — »), pas « 0,00 € ». Avec des nuits
                    finalisées, il les compte, et l'aide dit ce qui manque. */}
                <p className="fi-kpi__value">{formatMoney(closesDuMois.length === 0 && montantValide === null
                  ? null : sommeRetenue(closesDuMois) + (montantValide ?? 0))}</p>
                <p className="fi-kpi__label">
                  {[
                    closesDuMois.length > 0
                      ? `${closesDuMois.length} nuit${closesDuMois.length > 1 ? 's' : ''} finalisée${closesDuMois.length > 1 ? 's' : ''}` : null,
                    valideesDuMois.length > 0
                      ? `${valideesDuMois.length} validée${valideesDuMois.length > 1 ? 's' : ''} sans clôture` : null,
                  ].filter(Boolean).join(' · ')}
                </p>
                {valideesDuMois.length > 0 ? (
                  <p className="lc-hint">
                    {montantValide !== null
                      ? `Dont ${formatMoney(montantValide)} validés sans clôture détaillée.`
                      : 'Le montant des nuits validées sans clôture n’est pas lisible avec votre rôle : ce total ne le compte pas.'}
                  </p>
                ) : null}
                {restantesDuMois.length > 0 ? (
                  <p className="fi-provisoire">
                    {restantesDuMois.length} nuit{restantesDuMois.length > 1 ? 's' : ''} de ce
                    mois {restantesDuMois.length > 1 ? 'restent' : 'reste'} à finaliser :
                    ce total n’est pas encore le chiffre du mois.
                  </p>
                ) : null}
              </>
            )}
          </div>
        </section>

        {/* Espèces à déposer */}
        <section className="lc-panel fih-bloc">
          <div className="lc-panel__head">
            <span className="fih-head__icon" aria-hidden="true"><Banknote size={17} /></span>
            <h2 className="lc-panel__title">Espèces à déposer</h2>
            <span className="lc-panel__spacer" />
            <Link className="lc-btn lc-btn--ghost lc-btn--sm" href="/comptabilite/banque?vue=a_deposer">Voir les espèces à déposer</Link>
          </div>
          <div className="lc-panel__body">
            {depots.etat === 'chargement' ? <EnCours quoi="des remises" /> : null}
            {depots.etat === 'absent'
              ? <Indisponible titre="Remises indisponibles" message={depots.message} />
              : null}
            {depots.etat === 'ok' ? (
              <>
                <p className="fi-kpi__value"><Montant cents={depots.valeur.restant_cents} fort /></p>
                <p className="fi-kpi__label">Reste à porter en banque</p>
                {depots.valeur.nuits > 0 ? (
                  <p className="lc-hint">
                    Sur {depots.valeur.nuits} nuit{depots.valeur.nuits > 1 ? 's' : ''} finalisée{depots.valeur.nuits > 1 ? 's' : ''}.
                  </p>
                ) : (
                  <p className="lc-hint">Aucune nuit finalisée n'attend de remise.</p>
                )}
              </>
            ) : null}
          </div>
        </section>
      </div>

      {/* ═══════════ AJOUTER DES SOURCES ═══════════
          La zone de dépôt de l'écran des sources, sans en changer une ligne :
          même reconnaissance par le contenu, même verdict fichier par
          fichier. Une fenêtre plutôt qu'une page, parce qu'on y vient pour
          un geste de dix secondes et qu'on veut retrouver l'accueil à jour. */}
      <Modal open={depotOuvert} onClose={fermerDepot} owning ariaLabel="Ajouter des sources" width={960}>
        <header className="lc-modal__head">
          <span className="lc-modal__icon" aria-hidden="true"><FilePlus2 size={22} /></span>
          <div className="fih-depot__tete">
            <div className="lc-modal__title">Ajouter des sources</div>
            <p className="fih-depot__sous">
              Rapports de caisse ZAP, redditions Shotgun : chaque PDF est reconnu par son contenu,
              lu, puis rangé dans sa nuit.
            </p>
          </div>
          <button type="button" className="lc-btn lc-btn--ghost lc-btn--icon lc-modal__close"
            onClick={fermerDepot} disabled={depotEnCours} aria-label="Fermer la fenêtre">
            <X size={18} />
          </button>
        </header>
        <div className="lc-modal__body fiw-modal">
          <DepotRapports nu onFini={depotTermine} onOccupe={signalerDepot} />
          <div className="fiw-modal__actions fih-depot__pied">
            {depotEnCours ? (
              <span className="lc-hint fih-depot__attente">
                Dépôt en cours : la fenêtre pourra se fermer quand le dernier fichier aura été lu.
              </span>
            ) : null}
            {depotFait && !depotEnCours ? (
              <Link className="lc-btn lc-btn--ghost" href="/comptabilite/sources?vue=tout">
                Voir les documents reçus
              </Link>
            ) : null}
            <button type="button" className="lc-btn lc-btn--primary" onClick={fermerDepot} disabled={depotEnCours}>
              {depotFait ? 'Terminé' : 'Fermer'}
            </button>
          </div>
        </div>
      </Modal>
    </main>
  )
}
